import type { ReactNode } from 'react';

export interface MeetingSummarySourceConfig {
  id:
    | 'fathom'
    | 'google_meet'
    | 'microsoft_teams'
    | 'zoom'
    | 'granola'
    | 'fireflies';
  name: string;
  subCopy: string;
  icon: ReactNode;
  setupChecklist: { items: string[]; tip?: string };
  // Source-specific action target. Fathom uses Connect button (action.kind='oauth').
  // Others link to a settings section (action.kind='settings-link').
  action:
    | { kind: 'oauth' }
    | { kind: 'settings-link'; label: string; settingsPath: string };
}

export const MEETING_SUMMARY_SOURCES: readonly MeetingSummarySourceConfig[] =
  Object.freeze([
    {
      id: 'fathom',
      name: 'Fathom',
      subCopy:
        'Records and summarises your meetings, then sends them straight to Ahoy.',
      icon: (
        <img
          src='/assets/figma/fathom-icon.svg'
          alt=''
          className='size-6 object-contain'
        />
      ),
      setupChecklist: {
        items: [
          'A Fathom account with your calendar integration connected',
          'Auto-record turned on in Fathom settings',
          "You must be on the call – Fathom won't join without you",
        ],
      },
      action: { kind: 'oauth' },
    },
    {
      id: 'google_meet',
      name: 'Google Meet',
      subCopy:
        'Pulls transcripts and summaries from Meet through your connected Google Workspace.',
      icon: (
        <img
          src='/assets/figma/google-meet-icon.svg'
          alt=''
          className='size-6 object-contain'
        />
      ),
      setupChecklist: {
        items: [
          'A Google Workspace edition that supports transcripts (Business Standard or higher)',
          'Transcripts enabled by your Google Workspace admin',
          'Transcription started for each call you want captured',
          "Only the meeting host can start transcription – if you're not the host, ask them to start it",
          'Transcription is available in desktop browsers only',
        ],
        tip: 'To transcribe every meeting automatically, turn on "Transcribe the meeting" in your Google Calendar event under Video call options.',
      },
      action: {
        kind: 'settings-link',
        label: 'Email & Calendar',
        settingsPath: 'settings/email-calendar',
      },
    },
    {
      id: 'microsoft_teams',
      name: 'Microsoft Teams',
      subCopy:
        'Pulls recordings and transcripts from Teams through your connected Microsoft 365 account.',
      icon: (
        <img
          src='/assets/figma/teams-icon.svg'
          alt=''
          className='size-6 object-contain'
        />
      ),
      setupChecklist: {
        items: [
          'A Microsoft 365 Business or Enterprise subscription with Teams',
          'Call recording and transcription enabled by your Microsoft 365 admin',
          'Recording started for each call you want captured',
        ],
        tip: 'To record every meeting automatically, ask your Microsoft 365 admin to enable automatic recording in the Teams admin center.',
      },
      action: {
        kind: 'settings-link',
        label: 'Email & Calendar',
        settingsPath: 'settings/email-calendar',
      },
    },
    {
      id: 'zoom',
      name: 'Zoom',
      subCopy:
        'Reads Zoom AI Companion summary emails from your connected Gmail account.',
      icon: (
        <img
          src='/assets/figma/zoom-icon.svg'
          alt=''
          className='size-6 object-contain'
        />
      ),
      setupChecklist: {
        items: [
          'A Zoom Pro plan or higher',
          'AI Companion enabled on your Zoom account',
          'Meeting Summary enabled in your Zoom profile settings',
          'Auto-start enabled, or manually start the summary at the beginning of each call',
          '"Include the full summary text" turned on in your Zoom settings',
          'Summary shared with meeting invitees so it arrives in your inbox',
        ],
        tip: 'If you\'re not always the host, ask hosts to set "Automatically share summary with" to include meeting invitees in your organization.',
      },
      action: {
        kind: 'settings-link',
        label: 'Email & Calendar',
        settingsPath: 'settings/email-calendar',
      },
    },
    {
      id: 'granola',
      name: 'Granola',
      subCopy:
        'Connect your Granola account via MCP to sync meeting notes automatically and to make them available to query through Ahoy chat.',
      icon: (
        <img
          src='/assets/figma/granola-icon.svg'
          alt=''
          className='size-6 object-contain'
        />
      ),
      setupChecklist: {
        items: [
          'Your Granola account uses the same email as your Ahoy user',
          'The Granola desktop app is running during your meetings',
        ],
        tip: 'You can also share Granola note links in a Slack channel with the Ahoy bot – that works without connecting here.',
      },
      action: { kind: 'oauth' },
    },
    {
      id: 'fireflies',
      name: 'Fireflies',
      subCopy:
        'Connect your Fireflies account via MCP to sync meeting notes automatically and to make them available to query through Ahoy chat.',
      icon: (
        <img
          src='/assets/figma/fireflies-icon.svg'
          alt=''
          className='size-6 object-contain'
        />
      ),
      setupChecklist: {
        items: [
          'Your Fireflies account uses the same email as your Ahoy user',
          'Fireflies is set to record and transcribe your meetings',
        ],
      },
      action: { kind: 'oauth' },
    },
  ]);
